import type { PrintOptionsInfo } from './usePrintOptions';
import { useCallback, useEffect, useState } from 'react';

/**
 * Opens the print preview for a newly created invoice as soon as that invoice's print option shows up
 * in the print options query.
 *
 * The caller cannot simply await `refetch()` and read its result: the print options query is refetched
 * from several places at once (payment success, remote cache invalidation), and React Query cancels an
 * in-flight refetch when the next one starts. The awaited call then resolves with the last cached data
 * instead of the new data, so the invoice would be missing. Reacting to the query data makes the
 * preview open no matter which refetch ends up winning.
 *
 * The request never replaces a preview that is already on screen, so a slow refetch cannot push the
 * invoice over a preview the user opened in the meantime.
 *
 * @param printOptionsInfo print options of the entity the invoice was created for
 * @returns a callback that requests the preview for one invoice id
 */
// @ai-generated 2026-09-18T13:36:34+00:00 by matthias.gessinger@synqony.com using claude-opus-5[1m]
export function useInvoicePrintPreview(printOptionsInfo: PrintOptionsInfo) {
    const [pendingInvoiceId, setPendingInvoiceId] = useState<string>();
    const { printOptions, refetch, setCurrentPrintOption } = printOptionsInfo;

    useEffect(() => {
        if (pendingInvoiceId === undefined) {
            return;
        }

        const invoiceOption = printOptions.find(
            (option) => option.id === 'invoice' && option.related_object_id === pendingInvoiceId,
        );
        if (invoiceOption === undefined) {
            return;
        }

        setPendingInvoiceId(undefined);
        setCurrentPrintOption((current) => current ?? invoiceOption);
    }, [pendingInvoiceId, printOptions, setCurrentPrintOption]);

    return useCallback(
        (invoiceId: string) => {
            setPendingInvoiceId(invoiceId);
            void refetch();
        },
        [refetch],
    );
}
